import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, LoadingScreen } from '@cowoo/shared/ui-web';
import { formatDayShort, formatTime, toBerlinDay, todayBerlin, addDays } from '@cowoo/shared';
import { api } from '../../lib/api';
import { EmptyState, ListCard, Page, Table, Td, Th, fieldClass } from '../../ui';

/**
 * `/terminals/ereignisse` (10-web-admin § 15, `AdminDoorEvents`).
 *
 * The raw door log, newest first. The server filters by terminal and caps the
 * page; type and method are narrowed here because the list is small by design
 * — events older than 90 days are gone (D-093), and the note says so.
 *
 * The terminal's name comes with each row when the server sends it; an older
 * server sends the id only, and the column then shows the terminal that was
 * filtered on, or nothing.
 */

const PAGE = 50;
const MAX = 200;

const TYPES = [
  'entry',
  'exit_inferred',
  'exit_unknown',
  'denied',
  'door_open',
  'door_closed',
  'door_held_open',
  'tamper',
  'boot',
  'clock_invalid',
  'update',
] as const;
/** The methods of 05-door-protocol; a value an older row still carries is shown raw. */
const METHODS = ['ble', 'guest_qr', 'remote'] as const;
/** The denial reasons of 05-door-protocol § 9; anything else is shown raw. */
const REASONS = [
  'no_entitlement',
  'outside_hours',
  'pass_expired',
  'revoked',
  'bad_signature',
  'guest_invalid',
  'guest_wrong_day',
  'guest_wrong_location',
  'clock_invalid',
  'out_of_service',
] as const;

interface DoorEvent {
  id: string;
  ts: string;
  type: string;
  method: string | null;
  reason: string | null;
  userEmail: string | null;
  terminalId?: string;
  terminalLabel?: string | null;
  actor?: string | null;
}

export function DoorEvents() {
  const { t } = useTranslation();
  const [terminalId, setTerminalId] = useState('');
  const [type, setType] = useState('');
  const [method, setMethod] = useState('');
  const [limit, setLimit] = useState(PAGE);

  const terminals = useQuery({
    queryKey: ['admin', 'terminals'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/terminals');
      if (error) throw error;
      return data.items;
    },
  });

  const events = useQuery({
    queryKey: ['admin', 'door-events', terminalId, limit],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/door-events', {
        params: { query: { ...(terminalId ? { terminalId } : {}), limit } },
      });
      if (error) throw error;
      return data.items as DoorEvent[];
    },
  });

  if (events.isLoading && !events.data) return <LoadingScreen label={t('common.loading')} />;

  const labelOf = (event: DoorEvent): string =>
    event.terminalLabel ??
    terminals.data?.find((terminal) => terminal.id === (event.terminalId ?? terminalId))?.label ??
    '—';

  const rows = (events.data ?? []).filter(
    (event) => (type === '' || event.type === type) && (method === '' || event.method === method),
  );
  const canLoadMore = (events.data?.length ?? 0) >= limit && limit < MAX;

  return (
    <Page>
      <ListCard
        title={t('admin.doorEvents.title')}
        actions={
          <>
            <select
              value={terminalId}
              onChange={(event) => setTerminalId(event.target.value)}
              aria-label={t('admin.doorEvents.column.terminal')}
              className={`${fieldClass} h-9 w-auto`}
            >
              <option value="">{t('admin.doorEvents.allTerminals')}</option>
              {(terminals.data ?? []).map((terminal) => (
                <option key={terminal.id} value={terminal.id}>
                  {terminal.label}
                </option>
              ))}
            </select>
            <select
              value={type}
              onChange={(event) => setType(event.target.value)}
              aria-label={t('admin.doorEvents.column.type')}
              className={`${fieldClass} h-9 w-auto`}
            >
              <option value="">{t('admin.doorEvents.allTypes')}</option>
              {TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(`admin.doorEvents.type.${value}`)}
                </option>
              ))}
            </select>
            <select
              value={method}
              onChange={(event) => setMethod(event.target.value)}
              aria-label={t('admin.doorEvents.column.method')}
              className={`${fieldClass} h-9 w-auto`}
            >
              <option value="">{t('admin.doorEvents.allMethods')}</option>
              {METHODS.map((value) => (
                <option key={value} value={value}>
                  {t(`admin.doorEvents.method.${value}`)}
                </option>
              ))}
            </select>
          </>
        }
      >
        {rows.length === 0 ? (
          <EmptyState>{t('admin.doorEvents.empty')}</EmptyState>
        ) : (
          <Table
            head={
              <>
                <Th>{t('admin.doorEvents.column.time')}</Th>
                <Th>{t('admin.doorEvents.column.terminal')}</Th>
                <Th>{t('admin.doorEvents.column.type')}</Th>
                <Th>{t('admin.doorEvents.column.method')}</Th>
                <Th>{t('admin.doorEvents.column.person')}</Th>
                <Th>{t('admin.doorEvents.column.reason')}</Th>
              </>
            }
          >
            {rows.map((event) => (
              <tr key={event.id} className="hover:bg-fog">
                <Td bold>{whenLabel(event.ts, t)}</Td>
                <Td>{labelOf(event)}</Td>
                <Td>{typeLabel(event, t)}</Td>
                <Td>{methodLabel(event, t)}</Td>
                <Td>{event.actor ?? event.userEmail ?? '–'}</Td>
                <Td>{reasonLabel(event, t)}</Td>
              </tr>
            ))}
          </Table>
        )}

        <div className="flex items-center justify-between gap-3">
          <p className="text-adminSmall text-muted">{t('admin.doorEvents.retention')}</p>
          {canLoadMore ? (
            <Button
              size="sm"
              variant="secondary"
              loading={events.isFetching}
              onClick={() => setLimit((value) => Math.min(MAX, value + PAGE))}
            >
              {t('admin.doorEvents.more')}
            </Button>
          ) : null}
        </div>
      </ListCard>
    </Page>
  );
}

type T = ReturnType<typeof useTranslation>['t'];

/** "Heute, 09:31" / "Gestern, 17:44" / "Do, 17. Sep · 08:52" (03 § 8). */
function whenLabel(iso: string, t: T): string {
  const day = toBerlinDay(iso);
  const time = formatTime(iso);
  if (day === todayBerlin()) return t('admin.doorEvents.today', { time });
  if (day === addDays(todayBerlin(), -1)) return t('admin.doorEvents.yesterday', { time });
  return `${formatDayShort(day)} · ${time}`;
}

/** A door opened from the backoffice is its own row, as the board draws it. */
function typeLabel(event: DoorEvent, t: T): string {
  if (event.method === 'remote' && (event.type === 'door_open' || event.type === 'entry')) {
    return t('admin.doorEvents.remoteOpen');
  }
  const known = (TYPES as readonly string[]).includes(event.type);
  return known ? t(`admin.doorEvents.type.${event.type}`) : event.type;
}

function methodLabel(event: DoorEvent, t: T): string {
  if (!event.method) return '–';
  return (METHODS as readonly string[]).includes(event.method)
    ? t(`admin.doorEvents.method.${event.method}`)
    : event.method;
}

/** `denied` carries a protocol reason (05 § 9); a remote open carries the staff reason verbatim. */
function reasonLabel(event: DoorEvent, t: T): string {
  if (!event.reason) return '–';
  if (event.type === 'denied') {
    return (REASONS as readonly string[]).includes(event.reason)
      ? t(`admin.doorEvents.reason.${event.reason}`)
      : event.reason;
  }
  return event.reason;
}
